import { goto } from '$app/navigation';
import { page, navigating } from '$app/stores';
import { compile, match } from 'path-to-regexp';
import { derived, get, readonly, writable, type Writable } from 'svelte/store';
import { intercept } from './interceptor';
import type { TNavigateOptionsWithInvalidateAll, TRouteId } from './types';
import { getRouteFromPathname } from './getRouteFromPathname';
import type { TRouteParams } from '@casumo/fabric-fundamentals';
import { addQueryAndHash } from './utils';

const getRouteById = (routeId: TRouteId, params: TRouteParams = {}) => {
    const [newRouteId, newParams] = intercept(routeId, params);
    // There is a rather rare scenario page would error, then data.navigationSlugs would be undefined
    const route: string | undefined = get(page).data?.navigationSlugs?.[newRouteId as TRouteId];
    if (!route) return null;
    try {
        return compile(route, { encode: encodeURIComponent })(newParams);
    } catch (e) {
        return null;
    }
};

export const getCurrentRoute = () => ({
    ...getRouteFromPathname(get(page).url.pathname, get(page).data.navigationSlugs),
    hash: get(page).url.hash,
    query: get(page).url.searchParams.toString(),
    href: get(page).url.href,
    pathname: get(page).url.pathname
});

const navigateByRouteId = (routeId: TRouteId, options: TNavigateOptionsWithInvalidateAll = {}) => {
    const { replace: replaceState, params, query, hash, invalidateAll = false } = options;
    const route = getRouteById(routeId, params);
    if (!route) return Promise.resolve();

    const finalRoute = addQueryAndHash(route, query, hash);
    return goto(finalRoute, { replaceState, invalidateAll });
};

const compareRoutes = (first: string | null = '', second: string | null = '') => {
    if (!first || !second) return false;
    first = first.endsWith('/') ? first.slice(0, -1) : first;
    second = second.endsWith('/') ? second.slice(0, -1) : second;
    return first === second;
};

export const getRoute = getRouteById;
export const navigate = navigateByRouteId;
export const isActiveRoute = (routeId: TRouteId, params?: TRouteParams) => {
    return derived(
        [navigating, page],
        ([$navigating, $page], set) => {
            $navigating?.complete.then(
                () => {
                    if ($page.error) {
                        set(false);
                    } else {
                        const {
                            url: { pathname }
                        } = get(page);
                        set(compareRoutes(pathname, getRouteById(routeId, params)));
                    }
                },
                () => {} // swallow rejections - they do not matter here
            );
        },
        get(page).error
            ? false
            : compareRoutes(get(page).url.pathname, getRouteById(routeId, params))
    );
};

export const isActiveSlugRoute = (routeId: TRouteId) => {
    const [maybeRoute] = intercept(routeId, {});
    const route: string | undefined = get(page).data.navigationSlugs[maybeRoute as TRouteId];

    // TODO: Cover market reactivity if required

    if (!route) return readonly(writable(false));

    const matcher = match(route, { decode: decodeURIComponent });

    return derived(
        [navigating],
        ([$navigating], set) => {
            $navigating?.complete.then(
                () => {
                    const {
                        url: { pathname }
                    } = get(page);

                    set(Boolean(matcher(pathname)));
                },
                () => {} // swallow rejections - they do not matter here
            );
        },
        Boolean(matcher(get(page).url.pathname))
    );
};

export const previousUrl: Writable<URL | null> = writable(null);

export const navigationApi = {
    getRoute,
    navigate: navigateByRouteId,
    isActiveRoute,
    getCurrentRoute,
    getPreviousUrl: () => get(previousUrl)
};
